import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Paper,
  Alert, CircularProgress, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, Snackbar, InputAdornment, Tooltip,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { BarcodeOptions, DEFAULT_OPTIONS, BarcodeListItem, ProductLookupResult } from './types';
import DeleteIcon from '@mui/icons-material/Delete';
import { getPreviewSvg, generateBarcode, downloadBarcode, downloadBarcodePng, listBarcodes, deleteBarcode, getCheckDigit, lookupProduct } from './barcodeApi';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BarcodePreview from './BarcodePreview';
import BarcodeSettings from './BarcodeSettings';
import { API_BASE_URL } from '../../config';
import { SavedBarcodePreset, loadBarcodePresets, persistBarcodePresets } from './presetStorage';

interface BarcodeGeneratorPageProps {
  categoryIds?: string[];
}

const BarcodeGeneratorPage: React.FC<BarcodeGeneratorPageProps> = ({ categoryIds }) => {
  const [eanInput, setEanInput] = useState('');
  const [options, setOptions] = useState<BarcodeOptions>(DEFAULT_OPTIONS);
  const [svgPreview, setSvgPreview] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generatedAssetId, setGeneratedAssetId] = useState<string | null>(null);
  const [history, setHistory] = useState<BarcodeListItem[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [checkDigitInfo, setCheckDigitInfo] = useState<string | null>(null);
  const [artNrInput, setArtNrInput] = useState('');
  const [productLookup, setProductLookup] = useState<ProductLookupResult | null>(null);
  const [productLookupLoading, setProductLookupLoading] = useState(false);

  const [presets, setPresets] = useState<SavedBarcodePreset[]>(loadBarcodePresets);
  const [activeTab, setActiveTab] = useState('standard');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  const previewTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const artNrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const items = await listBarcodes();
      setHistory(items);
    } catch {}
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const updatePreview = useCallback(async (ean: string, opts: BarcodeOptions) => {
    const cleaned = ean.replace(/\s|-/g, '');
    if (cleaned.length < 12) {
      setSvgPreview(null);
      setPreviewError(null);
      setCheckDigitInfo(null);
      return;
    }

    if (cleaned.length === 12) {
      try {
        const result = await getCheckDigit(cleaned);
        setCheckDigitInfo(`Kontrollsiffra: ${result.checkDigit}`);
      } catch {
        setCheckDigitInfo(null);
      }
    } else {
      setCheckDigitInfo(null);
    }

    if (cleaned.length < 12 || cleaned.length > 13) {
      setSvgPreview(null);
      setPreviewError(cleaned.length > 13 ? 'EAN-13 max 13 siffror' : null);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const svg = await getPreviewSvg(cleaned, opts);
      setSvgPreview(svg);
    } catch (err: any) {
      setPreviewError(err.response?.data?.error || err.message || 'Kunde inte generera förhandsgranskning');
      setSvgPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (previewTimeout.current) clearTimeout(previewTimeout.current);
    previewTimeout.current = setTimeout(() => {
      updatePreview(eanInput, options);
    }, 400);
    return () => { if (previewTimeout.current) clearTimeout(previewTimeout.current); };
  }, [eanInput, options, updatePreview]);

  useEffect(() => {
    if (artNrTimeout.current) clearTimeout(artNrTimeout.current);
    const trimmed = artNrInput.trim();
    if (!trimmed) {
      setProductLookup(null);
      setProductLookupLoading(false);
      return;
    }
    setProductLookupLoading(true);
    artNrTimeout.current = setTimeout(async () => {
      try {
        const result = await lookupProduct(trimmed);
        setProductLookup(result);
      } catch {
        setProductLookup(null);
      } finally {
        setProductLookupLoading(false);
      }
    }, 500);
    return () => { if (artNrTimeout.current) clearTimeout(artNrTimeout.current); };
  }, [artNrInput]);

  const handleTabChange = (_: any, newTab: string) => {
    setActiveTab(newTab);
    if (newTab === 'standard') {
      setOptions(DEFAULT_OPTIONS);
    } else {
      const preset = presets.find(p => p.id === newTab);
      if (preset) {
        setOptions({ ...DEFAULT_OPTIONS, ...preset.options });
      }
    }
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    const newPreset: SavedBarcodePreset = {
      id: `preset_${Date.now()}`,
      name: presetName.trim(),
      options: { ...options },
      locked: false,
    };
    const updated = [...presets, newPreset];
    setPresets(updated);
    persistBarcodePresets(updated);
    setActiveTab(newPreset.id);
    setSaveDialogOpen(false);
    setPresetName('');
    setSnackbar({ open: true, message: `Inställning "${newPreset.name}" sparad`, severity: 'success' });
  };

  const handleToggleLock = (presetId: string) => {
    const updated = presets.map(p => p.id === presetId ? { ...p, locked: !p.locked } : p);
    setPresets(updated);
    persistBarcodePresets(updated);
  };

  const handleDeletePreset = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId);
    if (preset?.locked) return;
    const updated = presets.filter(p => p.id !== presetId);
    setPresets(updated);
    persistBarcodePresets(updated);
    if (activeTab === presetId) {
      setActiveTab('standard');
      setOptions(DEFAULT_OPTIONS);
    }
  };

  const handleGenerate = async () => {
    const cleaned = eanInput.replace(/\s|-/g, '');
    if (cleaned.length < 12 || cleaned.length > 13) {
      setSnackbar({ open: true, message: 'Ange 12 eller 13 siffror', severity: 'error' });
      return;
    }

    setGenerating(true);
    setGeneratedAssetId(null);
    try {
      const result = await generateBarcode(cleaned, options, artNrInput.trim() || undefined, categoryIds);
      setGeneratedAssetId(result.asset._id);
      const msg = result.replaced
        ? `Streckkod uppdaterad – ersatte befintlig ${cleaned}.tif`
        : `Streckkod skapad och sparad som ${cleaned}.tif`;
      setSnackbar({ open: true, message: msg, severity: 'success' });
      loadHistory();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Kunde inte generera streckkod',
        severity: 'error',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (assetId: string) => {
    try {
      await downloadBarcode(assetId);
    } catch {
      setSnackbar({ open: true, message: 'Kunde inte ladda ner filen', severity: 'error' });
    }
  };

  const handleDeleteBarcode = async (assetId: string) => {
    try {
      await deleteBarcode(assetId);
      setSnackbar({ open: true, message: 'EAN-kod borttagen', severity: 'success' });
      loadHistory();
    } catch {
      setSnackbar({ open: true, message: 'Kunde inte ta bort', severity: 'error' });
    }
  };

  const handleEanInput = (value: string) => {
    const digitsOnly = value.replace(/[^\d]/g, '').slice(0, 13);
    setEanInput(digitsOnly);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Box sx={{ px: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1, minHeight: 40,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              textTransform: 'none', minHeight: 34, borderRadius: '8px', mx: 0.3, px: 2,
              fontSize: '0.82rem', fontWeight: 500, transition: 'all 0.2s ease',
              '&.Mui-selected': { bgcolor: '#616161', color: '#fff' },
              '&:not(.Mui-selected)': { color: 'text.secondary', bgcolor: '#f0f0f0', '&:hover': { bgcolor: '#e0e0e0' } },
            },
          }}
        >
          <Tab label="Grund" value="standard" />
          {presets.map((p) => (
            <Tab
              key={p.id}
              value={p.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{p.name}</span>
                  <Tooltip title={p.locked ? 'Låst – klicka för att låsa upp' : 'Olåst – klicka för att låsa'}>
                    {p.locked ? (
                      <LockIcon sx={{ fontSize: 13, color: 'warning.light' }} onClick={(e) => { e.stopPropagation(); handleToggleLock(p.id); }} />
                    ) : (
                      <LockOpenIcon sx={{ fontSize: 13, opacity: 0.5, '&:hover': { opacity: 1 } }} onClick={(e) => { e.stopPropagation(); handleToggleLock(p.id); }} />
                    )}
                  </Tooltip>
                  {!p.locked && (
                    <Tooltip title="Ta bort">
                      <CloseIcon sx={{ fontSize: 13, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }} onClick={(e) => { e.stopPropagation(); handleDeletePreset(p.id); }} />
                    </Tooltip>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Tooltip title="Spara nuvarande inställningar">
          <IconButton size="small" onClick={() => setSaveDialogOpen(true)} sx={{ ml: 1 }}>
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: '1 1 60%', minWidth: 0 }}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                EAN-13 nummer
              </Typography>

              <TextField
                fullWidth
                label="EAN-13"
                placeholder="Ange 12 eller 13 siffror..."
                value={eanInput}
                onChange={(e) => handleEanInput(e.target.value)}
                variant="outlined"
                size="medium"
                InputProps={{
                  endAdornment: checkDigitInfo ? (
                    <InputAdornment position="end">
                      <Chip label={checkDigitInfo} size="small" color="info" variant="outlined" />
                    </InputAdornment>
                  ) : undefined,
                  sx: { fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: '0.15em' },
                }}
                helperText={
                  eanInput.length > 0
                    ? `${eanInput.length}/13 siffror${eanInput.length === 12 ? ' (kontrollsiffra beräknas automatiskt)' : ''}`
                    : 'Skriv in 12 siffror (kontrollsiffra beräknas) eller 13 siffror'
                }
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                label="Artikelnummer (valfritt)"
                placeholder="Koppla till en produkt..."
                value={artNrInput}
                onChange={(e) => setArtNrInput(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {productLookupLoading ? (
                        <CircularProgress size={18} />
                      ) : productLookup?.found ? (
                        <Tooltip title={`Kopplad till: ${productLookup.produktnamn}`}>
                          <LinkIcon color="success" fontSize="small" />
                        </Tooltip>
                      ) : artNrInput.trim() ? (
                        <Tooltip title="Ingen produkt hittad – artikelnr sparas ändå">
                          <LinkOffIcon color="disabled" fontSize="small" />
                        </Tooltip>
                      ) : null}
                    </InputAdornment>
                  ),
                }}
                helperText={
                  productLookup?.found
                    ? `✓ ${productLookup.produktnamn}`
                    : artNrInput.trim() && !productLookupLoading
                      ? 'Ingen produkt hittad – artikelnr sparas ändå som metadata'
                      : 'Koppla streckkoden till en befintlig produkt i systemet'
                }
                FormHelperTextProps={{
                  sx: productLookup?.found ? { color: 'success.main', fontWeight: 500 } : undefined,
                }}
                sx={{ mb: 3 }}
              />

              <BarcodePreview svg={svgPreview} loading={previewLoading} error={previewError} />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGenerate}
                  disabled={generating || eanInput.replace(/\s/g, '').length < 12}
                  startIcon={generating ? <CircularProgress size={20} color="inherit" /> : undefined}
                  sx={{ flex: 1 }}
                >
                  {generating ? 'Genererar...' : 'Generera TIFF och spara'}
                </Button>

                {generatedAssetId && (<>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleDownload(generatedAssetId)}
                    startIcon={<DownloadIcon />}
                  >
                    TIFF
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => downloadBarcodePng(generatedAssetId)}
                    startIcon={<DownloadIcon />}
                  >
                    PNG
                  </Button>
                </>)}
              </Box>

              {generatedAssetId && (
                <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                  {eanInput.replace(/\s/g, '')}.tif sparad i "EAN-koder" ({options.dpi} DPI). Finns redan en med samma nummer ersätts den automatiskt.
                </Alert>
              )}
            </Paper>

            {history.length > 0 && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <HistoryIcon color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Tidigare genererade streckkoder
                  </Typography>
                </Box>

                <List dense disablePadding>
                  {history.map((item) => (
                    <ListItem
                      key={item._id}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Ladda ner TIFF">
                            <IconButton size="small" onClick={() => handleDownload(item._id)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ladda ner PNG">
                            <IconButton size="small" onClick={() => downloadBarcodePng(item._id)}>
                              <DownloadIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ta bort">
                            <IconButton edge="end" size="small" onClick={() => handleDeleteBarcode(item._id)}>
                              <DeleteIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      {item.previewPaths?.thumbnail && (
                        <ListItemAvatar>
                          <Avatar
                            variant="rounded"
                            src={`${API_BASE_URL}${item.previewPaths.thumbnail}`}
                            sx={{ width: 48, height: 32, bgcolor: '#fff', border: '1px solid #e0e0e0' }}
                          >
                            EAN
                          </Avatar>
                        </ListItemAvatar>
                      )}
                      <ListItemText
                        primary={item.title}
                        secondary={`${new Date(item.uploadedAt).toLocaleDateString('sv-SE')} · ${item.metadata?.dpi || '600'} DPI · ${(item.fileSize / 1024).toFixed(0)} KB`}
                        primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          <Box sx={{ flex: '0 0 340px', minWidth: 280 }}>
            <Paper sx={{ p: 3, position: 'sticky', top: 16 }}>
              <BarcodeSettings options={options} onChange={setOptions} />
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Spara inställningar</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Namn på inställning"
            placeholder="T.ex. SC4 för flexo..."
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleSavePreset} disabled={!presetName.trim()}>
            Spara
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BarcodeGeneratorPage;
